import React, { useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import "./HeaderLogo.css";

const PIN_PATH =
  "M4 1.5H15.2C22 1.5 26.2 5.7 26.2 12.3C26.2 18.9 21.8 23.2 15.2 23.2H10.3V28.7L7.1 36.2L4 30.5V1.5Z";

function LogoPin({ colorClass }) {
  return (
    <span className={`logo-pin logo-pin--${colorClass}`} aria-hidden="true">
      <svg
        className="logo-pin__svg"
        viewBox="0 0 28 38"
        focusable="false"
      >
        <path className="logo-pin__body" d={PIN_PATH} />

        <g className="logo-eye">
          <circle className="logo-eye__white" cx="15" cy="12.2" r="6" />
          <circle className="logo-pupil" cx="15" cy="12.2" r="2.55" />
        </g>
      </svg>
    </span>
  );
}

export default function HeaderLogo() {
  const logoRef = useRef(null);

  useEffect(() => {
    const logo = logoRef.current;

    if (
      !logo ||
      typeof window === "undefined"
    ) {
      return undefined;
    }

    const finePointerQuery =
      window.matchMedia?.(
        "(hover: hover) and (pointer: fine)"
      );

    const reducedMotionQuery =
      window.matchMedia?.(
        "(prefers-reduced-motion: reduce)"
      );

    if (
      finePointerQuery?.matches === false ||
      reducedMotionQuery?.matches
    ) {
      return undefined;
    }

    const eyes = Array.from(
      logo.querySelectorAll(".logo-eye")
    )
      .map((eye) => ({
        eye,
        pupil:
          eye.querySelector(".logo-pupil"),
        centerX: 0,
        centerY: 0,
      }))
      .filter((item) => item.pupil);

    if (!eyes.length) {
      return undefined;
    }

    let animationFrameId = 0;
    let latestPointerX = 0;
    let latestPointerY = 0;
    let hasPointerPosition = false;
    let documentVisible =
      document.visibilityState !== "hidden";

    const refreshEyeCenters = () => {
      eyes.forEach((item) => {
        const rect =
          item.eye.getBoundingClientRect();

        item.centerX =
          rect.left + rect.width / 2;

        item.centerY =
          rect.top + rect.height / 2;
      });
    };

    const renderPupils = () => {
      animationFrameId = 0;

      if (
        !documentVisible ||
        !hasPointerPosition
      ) {
        return;
      }

      const radius = 3.1;

      eyes.forEach((item) => {
        const angle = Math.atan2(
          latestPointerY - item.centerY,
          latestPointerX - item.centerX
        );

        const offsetX =
          Math.cos(angle) * radius;

        const offsetY =
          Math.sin(angle) * radius;

        item.pupil.setAttribute(
          "transform",
          `translate(${offsetX.toFixed(
            2
          )} ${offsetY.toFixed(2)})`
        );
      });
    };

    const scheduleRender = () => {
      if (
        animationFrameId ||
        !documentVisible
      ) {
        return;
      }

      animationFrameId =
        window.requestAnimationFrame(
          renderPupils
        );
    };

    const handlePointerMove = (event) => {
      latestPointerX = event.clientX;
      latestPointerY = event.clientY;
      hasPointerPosition = true;
      scheduleRender();
    };

    const resetPupils = () => {
      hasPointerPosition = false;

      if (animationFrameId) {
        window.cancelAnimationFrame(
          animationFrameId
        );
        animationFrameId = 0;
      }

      eyes.forEach((item) => {
        item.pupil.removeAttribute(
          "transform"
        );
      });
    };

    const handleVisibilityChange = () => {
      documentVisible =
        document.visibilityState !==
        "hidden";

      if (!documentVisible) {
        resetPupils();
      }
    };

    refreshEyeCenters();

    const resizeObserver =
      "ResizeObserver" in window
        ? new window.ResizeObserver(
            refreshEyeCenters
          )
        : null;

    resizeObserver?.observe(logo);

    window.addEventListener(
      "pointermove",
      handlePointerMove,
      { passive: true }
    );

    window.addEventListener(
      "resize",
      refreshEyeCenters,
      { passive: true }
    );

    window.addEventListener(
      "blur",
      resetPupils
    );

    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange
    );

    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(
          animationFrameId
        );
      }

      resizeObserver?.disconnect();

      window.removeEventListener(
        "pointermove",
        handlePointerMove
      );

      window.removeEventListener(
        "resize",
        refreshEyeCenters
      );

      window.removeEventListener(
        "blur",
        resetPupils
      );

      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
    };
  }, []);

  return (
    <NavLink
      to="/"
      className="logo-link"
      aria-label="Mapapet"
    >
      <span
        ref={logoRef}
        className="logo mapapet-logo"
      >
        <span className="letter">m</span>
        <span className="letter">a</span>
        <LogoPin colorClass="blue" />
        <span className="letter">a</span>
        <LogoPin colorClass="pink" />
        <span className="letter">e</span>
        <span className="letter">t</span>
      </span>
    </NavLink>
  );
}
